/**
 * Job Tracking Guide
 * Understanding workflow stages, state machine transitions, and booking type flows
 * Updated to reflect Feature 009: Workshop Booking & Job-Tracking System
 */

import {
  GitBranch,
  ArrowRight,
  ArrowDown,
  Clock,
  Wrench,
  ClipboardCheck,
  Truck,
  CheckCircle2,
  Camera,
  MessageSquare,
  LogIn,
  Search,
  FileSearch,
  FileClock,
  PackageSearch,
  Car,
  XCircle,
  Users,
  Shield,
  BadgeCheck,
  AlertTriangle,
} from 'lucide-react';
import { pageStyles, cardStyles } from '../../lib/styles';

/**
 * Job states with their icons and colors (matches shared/types/job-states.ts)
 */
const jobStates = [
  {
    id: 'WAITING_FOR_ARRIVAL',
    name: 'Esperando Llegada',
    icon: Clock,
    color: 'bg-slate-50 text-slate-700 border-slate-200',
    description: 'Vehiculo programado, esperando que llegue al taller',
    nextStates: ['CHECKED_IN', 'CANCELLED_BEFORE_DROP_OFF'],
  },
  {
    id: 'CHECKED_IN',
    name: 'Recepcionado',
    icon: LogIn,
    color: 'bg-blue-50 text-blue-700 border-blue-200',
    description: 'Vehiculo recibido en el taller',
    nextStates: ['VALORATION'],
  },
  {
    id: 'VALORATION',
    name: 'Valoracion',
    icon: Search,
    color: 'bg-purple-50 text-purple-700 border-purple-200',
    description: 'Evaluacion del estado y danos del vehiculo',
    nextStates: ['PERITATION', 'AWAITING_APPROVAL', 'IN_PROGRESS'],
  },
  {
    id: 'PERITATION',
    name: 'Peritacion',
    icon: FileSearch,
    color: 'bg-orange-50 text-orange-700 border-orange-200',
    description: 'Valoracion por perito de la aseguradora',
    nextStates: ['AWAITING_APPROVAL'],
    insuranceOnly: true,
  },
  {
    id: 'AWAITING_APPROVAL',
    name: 'Pendiente Aprobacion',
    icon: FileClock,
    color: 'bg-amber-50 text-amber-700 border-amber-200',
    description: 'Esperando aprobacion del presupuesto',
    nextStates: ['IN_PROGRESS', 'CANCELLED_AFTER_DROP_OFF'],
  },
  {
    id: 'IN_PROGRESS',
    name: 'En Trabajo',
    icon: Wrench,
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200',
    description: 'Reparacion activa en curso',
    nextStates: ['WAITING_PARTS', 'FINALISING'],
  },
  {
    id: 'WAITING_PARTS',
    name: 'Esperando Piezas',
    icon: PackageSearch,
    color: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    description: 'Trabajo pausado esperando piezas',
    nextStates: ['IN_PROGRESS'],
    optional: true,
  },
  {
    id: 'FINALISING',
    name: 'Control Calidad',
    icon: ClipboardCheck,
    color: 'bg-teal-50 text-teal-700 border-teal-200',
    description: 'Verificacion final del trabajo realizado',
    nextStates: ['READY_TO_PICKUP'],
  },
  {
    id: 'READY_TO_PICKUP',
    name: 'Listo Entrega',
    icon: Car,
    color: 'bg-green-50 text-green-700 border-green-200',
    description: 'Vehiculo listo para recoger',
    nextStates: ['COMPLETED'],
  },
  {
    id: 'COMPLETED',
    name: 'Completado',
    icon: CheckCircle2,
    color: 'bg-gray-50 text-gray-600 border-gray-200',
    description: 'Trabajo finalizado y vehiculo entregado',
    nextStates: ['ARCHIVED'],
    terminal: true,
  },
];

/**
 * Booking type workflows
 */
const bookingTypes = [
  {
    id: 'private',
    name: 'Particular',
    icon: Users,
    color: 'bg-blue-500',
    description: 'Cliente particular sin seguro',
    flow: ['WAITING_FOR_ARRIVAL', 'CHECKED_IN', 'VALORATION', 'AWAITING_APPROVAL', 'IN_PROGRESS', 'FINALISING', 'READY_TO_PICKUP', 'COMPLETED'],
    notes: 'Puede saltar directamente a IN_PROGRESS si no requiere presupuesto',
  },
  {
    id: 'insurance',
    name: 'Seguro',
    icon: Shield,
    color: 'bg-orange-500',
    description: 'Trabajo cubierto por aseguradora',
    flow: ['WAITING_FOR_ARRIVAL', 'CHECKED_IN', 'VALORATION', 'PERITATION', 'AWAITING_APPROVAL', 'IN_PROGRESS', 'FINALISING', 'READY_TO_PICKUP', 'COMPLETED'],
    notes: 'OBLIGATORIO pasar por PERITATION (peritaje)',
    mandatory: ['PERITATION'],
  },
  {
    id: 'warranty',
    name: 'Garantia',
    icon: BadgeCheck,
    color: 'bg-green-500',
    description: 'Trabajo cubierto por garantia',
    flow: ['WAITING_FOR_ARRIVAL', 'CHECKED_IN', 'VALORATION', 'AWAITING_APPROVAL', 'IN_PROGRESS', 'FINALISING', 'READY_TO_PICKUP', 'COMPLETED'],
    notes: 'Puede incluir PERITATION si lo requiere la garantia',
  },
];

export function JobTracking() {
  return (
    <div className={pageStyles.container}>
      {/* Introduction */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <GitBranch className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Sistema de Seguimiento de Trabajos</span>
          </div>
        </div>
        <div className={`${cardStyles.content} prose prose-gray max-w-none`}>
          <p className="text-sm text-gray-600">
            El sistema de trabajos utiliza una <strong>maquina de estados</strong> que
            controla el flujo desde que se agenda una cita hasta que se entrega el vehiculo.
            El flujo varia segun el <strong>tipo de reserva</strong> (Particular, Seguro o Garantia).
          </p>
        </div>
      </div>

      {/* Visual Flow Diagram */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Diagrama de Flujo General</span>
        </div>
        <div className={cardStyles.content}>
          <div className="overflow-x-auto">
            <div className="min-w-[700px] p-4">
              {/* Flow diagram using CSS */}
              <div className="flex flex-col items-center gap-2">
                {/* Row 1: Start */}
                <div className="flex items-center justify-center gap-2">
                  <StateBox state={jobStates[0]} />
                </div>
                <ArrowDown className="h-4 w-4 text-gray-400" />

                {/* Row 2: Check-in */}
                <div className="flex items-center justify-center gap-2">
                  <StateBox state={jobStates[1]} />
                </div>
                <ArrowDown className="h-4 w-4 text-gray-400" />

                {/* Row 3: Valoration */}
                <div className="flex items-center justify-center gap-2">
                  <StateBox state={jobStates[2]} />
                </div>

                {/* Row 4: Branch point */}
                <div className="flex items-center justify-center gap-6 mt-2">
                  <div className="flex flex-col items-center">
                    <span className="text-[10px] text-orange-600 font-medium mb-1">Solo Seguro</span>
                    <ArrowDown className="h-4 w-4 text-orange-400" />
                    <StateBox state={jobStates[3]} small />
                    <ArrowDown className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-[10px] text-gray-500 font-medium mb-1">Todos</span>
                    <ArrowDown className="h-4 w-4 text-gray-400" />
                  </div>
                  <div className="flex flex-col items-center">
                    <span className="text-[10px] text-blue-600 font-medium mb-1">Particular (opcional)</span>
                    <ArrowDown className="h-4 w-4 text-blue-400 opacity-50" />
                  </div>
                </div>

                {/* Row 5: Approval */}
                <div className="flex items-center justify-center gap-2">
                  <StateBox state={jobStates[4]} />
                </div>
                <ArrowDown className="h-4 w-4 text-gray-400" />

                {/* Row 6: In Progress with optional parts */}
                <div className="flex items-center gap-4">
                  <StateBox state={jobStates[5]} />
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-yellow-500" />
                    <StateBox state={jobStates[6]} small />
                    <ArrowRight className="h-4 w-4 text-yellow-500 rotate-180" />
                  </div>
                </div>
                <ArrowDown className="h-4 w-4 text-gray-400" />

                {/* Row 7-9: Final stages */}
                <StateBox state={jobStates[7]} />
                <ArrowDown className="h-4 w-4 text-gray-400" />
                <StateBox state={jobStates[8]} />
                <ArrowDown className="h-4 w-4 text-gray-400" />
                <StateBox state={jobStates[9]} />
              </div>
            </div>
          </div>

          {/* Legend */}
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex flex-wrap gap-4 text-[10px]">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-orange-100 border border-orange-200" />
                <span className="text-gray-600">Obligatorio Seguro</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-yellow-100 border border-yellow-200" />
                <span className="text-gray-600">Estado Opcional</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200" />
                <span className="text-gray-600">Estado Terminal</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Booking Type Workflows */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Flujos por Tipo de Reserva</span>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-6">
            {bookingTypes.map((type) => {
              const Icon = type.icon;
              return (
                <div key={type.id} className="border rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`p-1.5 rounded ${type.color}`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900">{type.name}</h4>
                      <p className="text-xs text-gray-500">{type.description}</p>
                    </div>
                  </div>

                  {/* Flow visualization */}
                  <div className="flex flex-wrap items-center gap-1 mb-3">
                    {type.flow.map((stateId, idx) => {
                      const state = jobStates.find(s => s.id === stateId);
                      const isMandatory = type.mandatory?.includes(stateId);
                      return (
                        <div key={stateId} className="flex items-center">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium border ${state?.color} ${isMandatory ? 'ring-2 ring-orange-300' : ''}`}>
                            {state?.name}
                          </span>
                          {idx < type.flow.length - 1 && (
                            <ArrowRight className="h-3 w-3 text-gray-300 mx-0.5" />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {type.notes && (
                    <div className="flex items-start gap-2 text-xs">
                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-600">{type.notes}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* All Job States */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Estados del Trabajo (13 estados)</span>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-3">
            {jobStates.map((state) => {
              const Icon = state.icon;
              return (
                <div key={state.id} className="flex items-start gap-3 p-3 rounded-lg border border-gray-100 hover:bg-gray-50">
                  <div className={`p-2 rounded-lg ${state.color} border`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm text-gray-900">{state.name}</span>
                      {state.insuranceOnly && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-orange-100 text-orange-700">
                          Solo Seguro
                        </span>
                      )}
                      {state.optional && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-yellow-100 text-yellow-700">
                          Opcional
                        </span>
                      )}
                      {state.terminal && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-medium bg-gray-100 text-gray-600">
                          Terminal
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-600 mb-2">{state.description}</p>
                    {state.nextStates && state.nextStates.length > 0 && (
                      <div className="flex items-center gap-1 text-[10px] text-gray-500">
                        <ArrowRight className="h-3 w-3" />
                        <span>Puede ir a:</span>
                        {state.nextStates.map((nextId, idx) => {
                          const nextState = jobStates.find(s => s.id === nextId);
                          return (
                            <span key={nextId}>
                              <span className="text-gray-700 font-medium">{nextState?.name || nextId}</span>
                              {idx < state.nextStates.length - 1 && ', '}
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Cancellation states */}
            <div className="pt-3 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-700 mb-2">Estados de Cancelacion:</p>
              <div className="grid gap-2 sm:grid-cols-2">
                <div className="flex items-center gap-2 p-2 rounded bg-red-50 border border-red-100">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <div>
                    <span className="text-xs font-medium text-red-700">Cancelado (Sin Llegada)</span>
                    <p className="text-[10px] text-red-600">Antes de recibir el vehiculo</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 p-2 rounded bg-red-50 border border-red-100">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <div>
                    <span className="text-xs font-medium text-red-700">Cancelado (Post Llegada)</span>
                    <p className="text-[10px] text-red-600">Despues de recibir el vehiculo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Using the Job Board */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Tablero de Trabajos (Kanban)</span>
        </div>
        <div className={cardStyles.content}>
          <p className="text-sm text-gray-600 mb-4">
            El Tablero muestra <strong>9 columnas</strong> (los estados activos del trabajo).
            Los estados terminales (Completado, Archivado, Cancelado) no se muestran en el tablero.
          </p>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Wrench className="h-5 w-5 text-gray-600" />
                <h4 className="font-medium">Vista Kanban</h4>
              </div>
              <p className="text-sm text-gray-600">
                Cada columna representa un estado. Los trabajos se mueven de
                izquierda a derecha a medida que avanzan.
              </p>
            </div>
            <div className="p-4 border rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <ClipboardCheck className="h-5 w-5 text-gray-600" />
                <h4 className="font-medium">Acciones Rapidas</h4>
              </div>
              <p className="text-sm text-gray-600">
                Cada tarjeta muestra botones para avanzar al siguiente estado
                sin necesidad de abrir el detalle.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Funciones del Seguimiento</span>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded bg-blue-50">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Linea de Tiempo</h4>
                <p className="text-sm text-gray-600">
                  Cada cambio de estado queda registrado con fecha, hora y
                  usuario que lo realizo.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded bg-green-50">
                <MessageSquare className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Notas Publicas</h4>
                <p className="text-sm text-gray-600">
                  Anade notas visibles para el cliente en su pagina de
                  seguimiento. Ideal para informar del progreso.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded bg-purple-50">
                <Camera className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Fotos</h4>
                <p className="text-sm text-gray-600">
                  El cliente puede subir fotos del vehiculo. Puedes verlas y
                  gestionarlas desde el detalle de la cita.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded bg-orange-50">
                <Truck className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Enlace de Seguimiento</h4>
                <p className="text-sm text-gray-600">
                  Genera un enlace unico para que el cliente pueda ver el estado
                  de su reparacion en tiempo real.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tips */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Consejos</span>
        </div>
        <div className={cardStyles.content}>
          <ul className="space-y-2 text-sm text-gray-600">
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                Actualiza el estado tan pronto como cambie para mantener al
                cliente informado.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                Para trabajos de <strong>Seguro</strong>, siempre debe pasar por Peritacion
                antes de Aprobacion.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                Usa "Esperando Piezas" cuando el trabajo se pause por falta de materiales.
              </span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
              <span>
                El sistema notifica automaticamente al cliente en cambios clave
                (Recepcionado, Listo para Recoger, etc.).
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}

/**
 * State box component for flow diagram
 */
function StateBox({ state, small = false }: { state: typeof jobStates[0]; small?: boolean }) {
  const Icon = state.icon;
  return (
    <div className={`flex items-center gap-1.5 px-2 py-1 rounded border ${state.color} ${small ? 'text-[10px]' : 'text-xs'}`}>
      <Icon className={small ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
      <span className="font-medium whitespace-nowrap">{state.name}</span>
    </div>
  );
}

export default JobTracking;
